/** Auto-dobór briefu ze szkicu, gdy operator nic nie wybrał. */
import { recommendCta, type CloseMode, type CtaIntent } from "@/lib/carouselStudioMeta";

export type BriefPainRow = { id: string; keywords?: string[] };

export type BriefDraftCurrent = {
  pain: string;
  formatId: string;
  funnel: string;
  sku: string;
  styleId: string;
  ctaTouched: boolean;
  ctaIntent: string;
  closeMode: string;
  painPicked: boolean;
  formatPicked: boolean;
  funnelPicked: boolean;
  skuPicked: boolean;
  stylePicked: boolean;
};

export type BriefDraftResolved = {
  pain_id: string;
  format_id: string;
  funnel_stage: "tofu" | "mofu" | "bofu";
  sku: string;
  style_id: string;
  cta_intent: CtaIntent;
  close_mode: CloseMode;
};

const DEFAULT_SKU = "pakiet-ebookow-bonusy";
const DEFAULT_STYLE = "krem-kasia";

/** Fallback keywords gdy katalog jeszcze nie dojechał. */
const FALLBACK_PAIN_KEYWORDS: Record<string, string[]> = {
  B1: ["będę jak", "bede jak", "echo", "taka sama", "głos mamy", "glos mamy"],
  B2: ["telefon", "dziecko", "dzieci", "ekran", "adhd", "bodźc", "bodzc"],
  B3: ["tłumaczę", "tlumacze", "grzeczna", "przepraszam"],
  B4: ["wina", "powinnam", "zła córka", "zla corka"],
  B5: ["wygląd", "wyglad", "schudnij", "wstyd"],
  B6: ["babcia", "wnuczka", "wnuk"],
  B7: ["opieka", "demencja", "dps"],
  B10: ["tata", "ojciec"],
  B11: ["granica", "karta stop", "stop"],
  B12: ["krzyk", "awantura", "kłótnia", "klotnia"],
  B17: ["wino", "e-papieros", "bar", "histeryzuj"],
  B18: ["siostra", "brat", "rodzeństwo"],
};

function inferFormatId(draft: string): string {
  const raw = String(draft || "").trim();
  if (!raw) return "";
  const low = raw.toLowerCase();
  const lines = raw.split(/\r?\n/).map((ln) => ln.trim()).filter(Boolean);
  const numbered = lines.filter((ln) => /^\d+[.)]\s/.test(ln)).length;
  const questions = lines.filter((ln) => ln.endsWith("?")).length;
  const hasThenNow =
    (low.includes("kiedyś") || low.includes("kiedys")) &&
    (low.includes("teraz") || low.includes("dzisiaj"));
  if (hasThenNow) return "vs-echo";
  if (numbered >= 2) return "signals";
  if (questions >= 3) return "question-list";
  if (questions >= 1 && numbered >= 1) return "question-list";
  return "signals";
}

function inferPainId(draft: string, pains?: BriefPainRow[]): string {
  const low = String(draft || "").toLowerCase();
  if (!low.trim()) return "";
  const rows =
    pains && pains.length
      ? pains
      : Object.entries(FALLBACK_PAIN_KEYWORDS).map(([id, keywords]) => ({ id, keywords }));
  let best = "";
  let bestScore = 0;
  for (const row of rows) {
    const id = String(row.id || "").trim();
    if (!id) continue;
    const keys = row.keywords?.length ? row.keywords : FALLBACK_PAIN_KEYWORDS[id] || [];
    let score = 0;
    for (const key of keys) {
      const needle = String(key || "").toLowerCase().trim();
      if (needle && low.includes(needle)) score += needle.length >= 6 ? 2 : 1;
    }
    if (score > bestScore) {
      bestScore = score;
      best = id;
    }
  }
  return bestScore > 0 ? best : "";
}

function inferFunnel(draft: string, formatId: string): "tofu" | "mofu" | "bofu" {
  const low = String(draft || "").toLowerCase();
  if (/\b(99\s*zł|99\s*zl|pakiet|kup|cena)\b/.test(low)) return "bofu";
  if (formatId === "question-list" || formatId === "steps-save" || formatId === "signals") return "mofu";
  if (/\b(narzędz|narzedz|karta stop|zapisz|checklist)\b/.test(low)) return "mofu";
  if (low.length < 80 && low.includes("?")) return "tofu";
  return "mofu";
}

export function resolveBriefFromDraft(
  draft: string,
  current: BriefDraftCurrent,
  pains?: BriefPainRow[],
): BriefDraftResolved {
  const inferredFormat = inferFormatId(draft);
  const format_id = current.formatPicked ? current.formatId : current.formatId || inferredFormat;
  const inferredPain = inferPainId(draft, pains);
  const pain_id = current.painPicked ? current.pain : inferredPain;
  const funnel_stage = current.funnelPicked
    ? ((current.funnel as "tofu" | "mofu" | "bofu") || "mofu")
    : inferFunnel(draft, format_id);
  const sku = current.skuPicked ? current.sku || DEFAULT_SKU : current.sku || DEFAULT_SKU;
  const style_id = current.stylePicked
    ? current.styleId || DEFAULT_STYLE
    : current.styleId || DEFAULT_STYLE;
  const rec = recommendCta(pain_id, format_id);
  const cta_intent =
    current.ctaTouched && current.ctaIntent
      ? (current.ctaIntent as CtaIntent)
      : rec.cta_intent;
  const close_mode =
    current.ctaTouched && current.closeMode
      ? (current.closeMode as CloseMode)
      : rec.close_mode;
  return {
    pain_id,
    format_id,
    funnel_stage,
    sku,
    style_id,
    cta_intent,
    close_mode,
  };
}
